import {useCallback, useEffect, useRef, useState} from "react";
import {graphql} from "react-relay";
import {fetchQuery} from "relay-runtime";
import environment from "../Environment";
import {useUserBrandsQuery} from "./__generated__/useUserBrandsQuery.graphql";

export type UserBrand = {
  id: string,
  name: string
};

export type UserBrandsStatus = "idle" | "loading" | "success" | "error";

const userBrandsQuery = graphql`
  query useUserBrandsQuery {
    currentUser {
      id
      brandSet {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;

/**
 * Lazily loads the brands the current user has access to.
 *
 * Nothing is fetched until `load()` is called. Subsequent calls are no-ops while a request is in flight
 * or after a successful load, so it is safe to call `load()` eagerly (e.g. on hover and on open).
 * After a failure, calling `load()` again retries the request.
 */
export default function useUserBrands() {
  const [brands, setBrands] = useState<UserBrand[]>([]);
  const [status, setStatus] = useState<UserBrandsStatus>("idle");
  const statusRef = useRef<UserBrandsStatus>("idle");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (statusRef.current === "loading" || statusRef.current === "success") {
      return;
    }

    statusRef.current = "loading";
    setStatus("loading");

    fetchQuery<useUserBrandsQuery>(environment, userBrandsQuery, {})
      .subscribe({
        next: (response) => {
          const brandSet = response && response.currentUser ? response.currentUser.brandSet : null;
          const nextBrands: UserBrand[] = [];
          if (brandSet) {
            brandSet.edges.forEach((edge) => {
              if (edge && edge.node) {
                nextBrands.push({id: edge.node.id, name: edge.node.name});
              }
            });
          }
          statusRef.current = "success";
          if (isMountedRef.current) {
            setBrands(nextBrands);
            setStatus("success");
          }
        },
        error: () => {
          statusRef.current = "error";
          if (isMountedRef.current) {
            setStatus("error");
          }
        }
      });
  }, []);

  return {brands, status, load};
}
